import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/TtnContainer.css';
import { REACT_APP_API_URL } from '../config';
import { ttnStampedPdfAPI } from '../http/ttnAPI';

type PosPart = { cxFrac: number; cyFrac: number; widthPt: number };
export type StampPos = { stamp: PosPart; sign: PosPart };

type Props = {
    ttnId: number;
    /** media-относительный путь к текущему PDF накладной. */
    pdfFile: string;
    stampFile: string | null;
    signFile: string | null;
    /** Начальное положение (сохранённое на ТТН либо дефолт с сервера). */
    initial: StampPos;
    onDone: () => void;
    onCancel: () => void;
};

const WIDTH_MIN = 40;
const WIDTH_MAX = 260;
const CANVAS_MAX_W = 900;

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const mediaUrl = (rel: string) => `${REACT_APP_API_URL}media/${rel}`;

const TtnStampPlacer: React.FC<Props> = ({ ttnId, pdfFile, stampFile, signFile, initial, onDone, onCancel }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);

    const [ready, setReady] = useState(false);
    const [renderErr, setRenderErr] = useState<string | null>(null);
    const [pagePt, setPagePt] = useState({ w: 1, h: 1 });   // размер страницы в пунктах
    const [dispPx, setDispPx] = useState({ w: 1, h: 1 });    // размер канваса на экране, px
    const [pageCount, setPageCount] = useState(1);

    const [stamp, setStamp] = useState<PosPart>(initial.stamp);
    const [sign, setSign] = useState<PosPart>(initial.sign);
    const [active, setActive] = useState<'stamp' | 'sign'>(stampFile ? 'stamp' : 'sign');
    const [save, setSave] = useState(false);
    const [busy, setBusy] = useState(false);

    // Пропорции картинок (h/w) — только для экранного превью; реальную высоту
    // на PDF считает сервер по самой картинке.
    const [stampAspect, setStampAspect] = useState(1);
    const [signAspect, setSignAspect] = useState(0.4);

    // --- отрисовка последней страницы PDF через pdf.js ---------------------
    // Печать/подпись сервер всегда кладёт на ПОСЛЕДНЮЮ страницу (см.
    // ttnStamp.ts: pages[pages.length - 1]) — превью должно показывать ту же
    // страницу, иначе координаты, выбранные на первой, не совпадут с тем,
    // куда картинки лягут на многостраничной накладной.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const pdfjs: any = await import('pdfjs-dist');
                pdfjs.GlobalWorkerOptions.workerSrc = (process.env.PUBLIC_URL || '') + '/pdf.worker.min.js';
                const doc = await pdfjs.getDocument(mediaUrl(pdfFile)).promise;
                setPageCount(doc.numPages);
                const page = await doc.getPage(doc.numPages);
                const vp1 = page.getViewport({ scale: 1 });
                const maxW = Math.min(CANVAS_MAX_W, wrapRef.current?.clientWidth || CANVAS_MAX_W);
                const scale = Math.max(0.2, maxW / vp1.width);
                const vp = page.getViewport({ scale });
                const canvas = canvasRef.current;
                if (!canvas || cancelled) return;
                canvas.width = Math.round(vp.width);
                canvas.height = Math.round(vp.height);
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                await page.render({ canvasContext: ctx, viewport: vp }).promise;
                if (cancelled) return;
                setPagePt({ w: vp1.width, h: vp1.height });
                setDispPx({ w: canvas.width, h: canvas.height });
                setReady(true);
            } catch (e: any) {
                if (!cancelled) setRenderErr('Не удалось отрисовать PDF: ' + (e?.message || String(e)));
            }
        })();
        return () => { cancelled = true; };
    }, [pdfFile]);

    useEffect(() => {
        if (!stampFile) return;
        const im = new Image();
        im.onload = () => setStampAspect(im.naturalHeight / im.naturalWidth || 1);
        im.src = mediaUrl(stampFile);
    }, [stampFile]);
    useEffect(() => {
        if (!signFile) return;
        const im = new Image();
        im.onload = () => setSignAspect(im.naturalHeight / im.naturalWidth || 0.4);
        im.src = mediaUrl(signFile);
    }, [signFile]);

    const setPart = (which: 'stamp' | 'sign', patch: Partial<PosPart>) => {
        (which === 'stamp' ? setStamp : setSign)((p) => ({ ...p, ...patch }));
    };

    // клик по канвасу — переносим центр активной картинки в точку клика
    const onCanvasClick = (e: React.MouseEvent) => {
        if (!ready) return;
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        setPart(active, { cxFrac: clamp01(px / dispPx.w), cyFrac: clamp01(1 - py / dispPx.h) });
    };

    // перетаскивание картинки мышью
    const dragRef = useRef<{ which: 'stamp' | 'sign' } | null>(null);
    const onBoxMouseDown = (which: 'stamp' | 'sign') => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setActive(which);
        dragRef.current = { which };
    };
    const onDragMove = useCallback((e: MouseEvent) => {
        const d = dragRef.current;
        const canvas = canvasRef.current;
        if (!d || !canvas) return;
        const rect = canvas.getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        setPart(d.which, { cxFrac: clamp01(px / rect.width), cyFrac: clamp01(1 - py / rect.height) });
    }, []);
    const onDragEnd = useCallback(() => { dragRef.current = null; }, []);
    useEffect(() => {
        window.addEventListener('mousemove', onDragMove);
        window.addEventListener('mouseup', onDragEnd);
        return () => {
            window.removeEventListener('mousemove', onDragMove);
            window.removeEventListener('mouseup', onDragEnd);
        };
    }, [onDragMove, onDragEnd]);

    const boxStyle = (p: PosPart, aspect: number): React.CSSProperties => {
        const wPx = (p.widthPt / pagePt.w) * dispPx.w;
        const hPx = wPx * aspect;
        return {
            left: p.cxFrac * dispPx.w - wPx / 2,
            top: (1 - p.cyFrac) * dispPx.h - hPx / 2,
            width: wPx,
            height: hPx,
        };
    };

    const activePart = active === 'stamp' ? stamp : sign;

    const submit = async () => {
        setBusy(true);
        const res: any = await ttnStampedPdfAPI(ttnId, {
            stampCxFrac: stamp.cxFrac, stampCyFrac: stamp.cyFrac, stampWidthPt: Math.round(stamp.widthPt),
            signCxFrac: sign.cxFrac, signCyFrac: sign.cyFrac, signWidthPt: Math.round(sign.widthPt),
            save: save ? 1 : 0,
        });
        setBusy(false);

        const blob: Blob | undefined = res?.data instanceof Blob ? res.data : undefined;
        if (res?.status !== 200 || !blob) {
            let msg = 'Не удалось сформировать PDF с печатью и подписью';
            try {
                const errBlob: Blob | undefined = res?.response?.data instanceof Blob ? res.response.data : blob;
                if (errBlob) msg = (await errBlob.text()).replace(/^"|"$/g, '') || msg;
            } catch {
                /* общее сообщение */
            }
            window.alert(msg);
            return;
        }
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        onDone();
    };

    return (
        <div className="ttnPlacer">
            <div className="ttnPlacer__hint">
                Кликните по документу, куда поставить картинку, либо перетащите её. Активная выделена рамкой.
                {pageCount > 1 && ` Показана последняя страница (${pageCount} из ${pageCount}) — печать и подпись всегда ставятся на неё.`}
            </div>

            <div className="ttnPlacer__toolbar">
                <div className="ttnPlacer__seg">
                    <button
                        type="button"
                        className={`nav__button ${active === 'stamp' ? 'btn-primary' : 'btn-ghost'}`}
                        disabled={!stampFile}
                        onClick={() => setActive('stamp')}
                    >
                        Печать
                    </button>
                    <button
                        type="button"
                        className={`nav__button ${active === 'sign' ? 'btn-primary' : 'btn-ghost'}`}
                        disabled={!signFile}
                        onClick={() => setActive('sign')}
                    >
                        Подпись
                    </button>
                </div>

                <label className="ttnPlacer__size">
                    <span>Размер: {Math.round(activePart.widthPt)} pt</span>
                    <input
                        type="range"
                        min={WIDTH_MIN}
                        max={WIDTH_MAX}
                        value={Math.round(activePart.widthPt)}
                        onChange={(e) => setPart(active, { widthPt: Number(e.target.value) })}
                    />
                </label>

                <label className="ttnPlacer__save" title="Запомнится для получателя платежа и применится ко всем его ТТН">
                    <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} />
                    Сохранить положение для получателя платежа
                </label>
            </div>

            <div className="ttnPlacer__canvas-wrap" ref={wrapRef}>
                {renderErr && <div className="ttnForm__error">{renderErr}</div>}
                <div className="ttnPlacer__stage">
                    <canvas ref={canvasRef} onClick={onCanvasClick} />
                    {ready && stampFile && (
                        <div
                            className={`ttnPlacer__box ${active === 'stamp' ? 'ttnPlacer__box--active' : 'ttnPlacer__box--idle'}`}
                            style={boxStyle(stamp, stampAspect)}
                            onMouseDown={onBoxMouseDown('stamp')}
                            title="Печать — перетащите"
                        >
                            <img src={mediaUrl(stampFile)} alt="печать" />
                        </div>
                    )}
                    {ready && signFile && (
                        <div
                            className={`ttnPlacer__box ${active === 'sign' ? 'ttnPlacer__box--active' : 'ttnPlacer__box--idle'}`}
                            style={boxStyle(sign, signAspect)}
                            onMouseDown={onBoxMouseDown('sign')}
                            title="Подпись — перетащите"
                        >
                            <img src={mediaUrl(signFile)} alt="подпись" />
                        </div>
                    )}
                </div>
            </div>

            <div className="ttnPlacer__footer">
                <button type="button" className="nav__button btn-primary" disabled={!ready || busy} onClick={submit}>
                    {busy ? 'Формирование…' : 'Сформировать PDF'}
                </button>
                <button type="button" className="nav__button btn-ghost" disabled={busy} onClick={onCancel}>
                    Отмена
                </button>
            </div>
        </div>
    );
};

export default TtnStampPlacer;
